import React, { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import { useMediaQuery } from 'react-responsive';
import { Image, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FiChevronDown, FiSettings } from 'react-icons/fi';
import Logo from '../../assets/images/brand/logo/logo-black.png';
import ROUTES from '../../constants/routes';
import { isBooleanFalse, isListEmpty, isListNotEmpty, isStringEmpty, isStringNotEmpty, isBooleanTrue } from '../../utils/objectUtils';
import { ReactComponent as CollapseExpandIcon } from '../../assets/icons/nav_sidebar_collapse-and-expand_icon.svg';
import styles from './NavbarVertical.module.css';
import { ReactComponent as HomeIcon } from '../../assets/icons/home_icon_nav_panel.svg';
import { ReactComponent as TreatmentsIcon } from '../../assets/icons/iconly_syringe.svg';
import { ReactComponent as LabsIcon } from '../../assets/icons/labs_nav_icon_flask.svg';
import { ReactComponent as MessagesIcon } from '../../assets/icons/iconly_chat.svg';
import { ReactComponent as ProgressLogIcon } from '../../assets/icons/progress_logs_graph_icon.svg';
import { ReactComponent as MealPlansIcon } from '../../assets/icons/meal_plans_icon_nav_panel.svg';
import { ReactComponent as SupportGroupsIcon } from '../../assets/icons/support_ticket_icon.svg';
import { ReactComponent as ReferralProgramIcon } from '../../assets/icons/referral_program_icon_nav_panel.svg';
import { ReactComponent as StoreIcon } from '../../assets/icons/store_icon_nav_panel.svg';
import { ReactComponent as SupportIcon } from '../../assets/icons/support_ticket_icon.svg';

const NAV_ICON_MAP = {
	'Home': HomeIcon,
	'My Treatments': TreatmentsIcon,
	'Labs': LabsIcon,
	'Messages': MessagesIcon,
	'Progress log': ProgressLogIcon,
	'Meal plans': MealPlansIcon,
	'Support groups': SupportGroupsIcon,
	'Referral program': ReferralProgramIcon,
	'Store': StoreIcon,
	'Support': SupportIcon,
	'Account management': FiSettings,
};

/**
 * Renders the V2 account sidebar navigation with responsive mobile behavior,
 * active route highlighting, and collapsed desktop tooltips.
 *
 * @param {object} props
 * @param {(show: boolean) => void} props.onClick - Controls mobile sidebar visibility.
 * @param {boolean} props.showMenu - Whether the mobile sidebar is open.
 * @param {boolean} props.collapsed - Whether the desktop sidebar is collapsed.
 * @param {() => void} props.onToggleCollapse - Toggles the desktop collapsed state.
 * @param {Array<object>} [props.menuOptions=[]] - Menu items generated for the current account user.
 * @returns {JSX.Element} The V2 vertical account navigation.
 */
const NavbarVertical = ({ onClick, showMenu, collapsed, onToggleCollapse, menuOptions = [] }) => {
	const [searchParams] = useSearchParams();
	const location = useLocation();
	const isMobile = useMediaQuery({ maxWidth: 767 });

	// Reset group toggles when navigating so the active route's parent opens by default.
	const [groupToggles, setGroupToggles] = useState({ pathname: null, groups: {} });
	const activeGroupToggles = groupToggles.pathname === location.pathname ? groupToggles.groups : {};

	const handleNavClick = () => {
		if (isBooleanTrue(isMobile)) {
			onClick(false);
		}
	};

	const flatMenuItems = menuOptions
		.filter((item) => !isBooleanFalse(item.show))
		.filter((item) => !item.grouptitle);

	const isPathActive = (link) => {
		if (isStringEmpty(link)) return false;
		if (location.pathname === link) return true;
		if (link !== ROUTES.ACCOUNT && location.pathname.startsWith(link + '/')) return true;
		return false;
	};

	/**
	 * Determines whether any child route should mark the parent menu item active.
	 *
	 * @param {Array<object>} items - Nested menu items to evaluate.
	 * @returns {boolean} Whether a child item matches the current route.
	 */
	const hasActiveChild = (items) => {
		for (const item of items) {
			if (isPathActive(item.link)) return true;
			if (item.children && hasActiveChild(item.children)) return true;
		}
		return false;
	};

	const renderNavItem = (menu, index) => {
		if (isListNotEmpty(menu.children) && isStringEmpty(menu.link)) {
			return renderGroupItem(menu, index);
		}

		return renderLinkItem(menu, index, isPathActive(menu.link));
	};

	/**
	 * Resolves a group's open state, preferring the patient's explicit toggle and
	 * otherwise opening whichever group owns the current route.
	 *
	 * @param {object} menu - Menu group with children.
	 * @returns {boolean} Whether the group's children should render.
	 */
	const isGroupExpanded = (menu) => {
		const toggledState = activeGroupToggles[menu.title];
		// undefined means the user hasn't toggled yet, so fall through to route default
		if (isBooleanTrue(toggledState)) return true;
		if (isBooleanFalse(toggledState)) return false;
		return hasActiveChild(menu.children);
	};

	const setGroupToggle = (menu, isOpen) => {
		setGroupToggles((previous) => ({
			pathname: location.pathname,
			groups: { ...(previous.pathname === location.pathname ? previous.groups : {}), [menu.title]: isOpen },
		}));
	};

	const handleGroupToggle = (menu, isExpanded) => {
		// collapsed sidebar can't show children, so expand it first
		if (isBooleanTrue(collapsed) && isBooleanFalse(isMobile)) {
			onToggleCollapse();
			setGroupToggle(menu, true);
			return;
		}

		setGroupToggle(menu, isBooleanFalse(isExpanded));
	};

	const renderGroupItem = (menu, index) => {
		// Only an explicit `show: false` hides a child; undefined means visible.
		const groupChildren = menu.children.filter((child) => !isBooleanFalse(child.show));
		if (isListEmpty(groupChildren)) return null;

		const isExpanded = isGroupExpanded(menu);
		const isCollapsedDesktop = isBooleanTrue(collapsed) && isBooleanFalse(isMobile);
		const toggleClasses = [
			styles.sidebarNavLink,
			isBooleanTrue(hasActiveChild(groupChildren)) ? styles.sidebarNavLinkActive : '',
		]
			.filter(isStringNotEmpty)
			.join(' ');

		const IconComponent = NAV_ICON_MAP[menu.title];
		const groupId = `nav-group-${index}`;

		const toggle = (
			<button
				type="button"
				className={toggleClasses}
				onClick={() => handleGroupToggle(menu, isExpanded)}
				aria-expanded={isBooleanFalse(isCollapsedDesktop) && isBooleanTrue(isExpanded)}
				aria-controls={groupId}
			>
				<span className={styles.sidebarNavIcon}>
					{IconComponent ? <IconComponent /> : null}
				</span>
				<span className={`${styles.sidebarNavLabel} ${isBooleanTrue(isCollapsedDesktop) ? styles.sidebarNavLabelHidden : ''}`}>
					{menu.title}
				</span>
				{isBooleanFalse(isCollapsedDesktop) && (
					<FiChevronDown
						className={`${styles.sidebarNavChevron} ${isBooleanTrue(isExpanded) ? styles.sidebarNavChevronOpen : ''}`}
						aria-hidden="true"
					/>
				)}
			</button>
		);

		return (
			<li key={index} className={styles.sidebarNavItem}>
				{isBooleanTrue(isCollapsedDesktop) ? (
					<OverlayTrigger
						placement="right"
						overlay={<Tooltip id={`nav-tooltip-${index}`}>{menu.title}</Tooltip>}
					>
						{toggle}
					</OverlayTrigger>
				) : toggle}
				{isBooleanFalse(isCollapsedDesktop) && isBooleanTrue(isExpanded) && (
					<ul className={styles.sidebarNavSubList} id={groupId}>
						{groupChildren.map((child, childIndex) => renderGroupChild(child, `${index}-${childIndex}`))}
					</ul>
				)}
			</li>
		);
	};

	const renderGroupChild = (child, key) => {
		const childClasses = [
			styles.sidebarNavSubLink,
			isBooleanTrue(isPathActive(child.link)) ? styles.sidebarNavSubLinkActive : '',
		]
			.filter(isStringNotEmpty)
			.join(' ');

		const content = isStringNotEmpty(child.externalLink) ? (
			<a
				href={child.externalLink}
				target="_blank"
				rel="noopener noreferrer"
				className={childClasses}
				onClick={handleNavClick}
			>
				{child.title}
			</a>
		) : isStringNotEmpty(child.link) ? (
			<Link
				to={{ pathname: child.link, search: searchParams.toString() }}
				className={childClasses}
				onClick={handleNavClick}
			>
				{child.title}
			</Link>
		) : null;

		if (!content) return null;

		return (
			<li key={key} className={styles.sidebarNavSubItem}>
				{content}
			</li>
		);
	};

	const renderLinkItem = (menu, index, isActive) => {
		const linkClasses = [
			styles.sidebarNavLink,
			isBooleanTrue(isActive) ? styles.sidebarNavLinkActive : '',
		]
			.filter(isStringNotEmpty)
			.join(' ');

		const IconComponent = NAV_ICON_MAP[menu.title];
		const iconElement = (
			<span className={styles.sidebarNavIcon}>
				{IconComponent ? (
					<IconComponent />
				) : menu.icon ? (
					React.cloneElement(menu.icon, { className: '', style: { fontSize: '1.125rem' } })
				) : null}
			</span>
		);

		const labelElement = (
			<span className={`${styles.sidebarNavLabel} ${isBooleanTrue(collapsed) && isBooleanFalse(isMobile) ? styles.sidebarNavLabelHidden : ''}`}>
				{menu.title}
			</span>
		);
		const badgeElement = menu.badge && isBooleanFalse(isMobile) && isBooleanFalse(collapsed) ? (
			<span className={styles.sidebarNavBadge}>
				{menu.badge}
			</span>
		) : null;

		const content = isStringNotEmpty(menu.externalLink) ? (
			<a
				href={menu.externalLink}
				target="_blank"
				rel="noopener noreferrer"
				className={linkClasses}
				onClick={handleNavClick}
			>
				{iconElement}
				{labelElement}
				{badgeElement}
			</a>
		) : isStringNotEmpty(menu.link) ? (
			<Link
				to={{ pathname: menu.link, search: searchParams.toString() }}
				className={linkClasses}
				onClick={handleNavClick}
			>
				{iconElement}
				{labelElement}
				{badgeElement}
			</Link>
		) : null;

		if (!content) return null;

		if (isBooleanTrue(collapsed) && isBooleanFalse(isMobile)) {
			return (
				<li key={index} className={styles.sidebarNavItem}>
					<OverlayTrigger
						placement="right"
						overlay={<Tooltip id={`nav-tooltip-${index}`}>{menu.title}</Tooltip>}
					>
						{content}
					</OverlayTrigger>
				</li>
			);
		}

		return (
			<li key={index} className={styles.sidebarNavItem}>
				{content}
			</li>
		);
	};

	const sidebarClasses = [
		styles.sidebar,
		isBooleanTrue(collapsed) && isBooleanFalse(isMobile) ? styles.sidebarCollapsed : '',
		isBooleanTrue(isMobile) && isBooleanTrue(showMenu) ? styles.sidebarMobileOpen : '',
	]
		.filter(isStringNotEmpty)
		.join(' ');

	return (
		<React.Fragment>
			{isBooleanTrue(isMobile) && (
				<div
					className={`${styles.sidebarOverlay} ${isBooleanTrue(showMenu) ? styles.sidebarOverlayVisible : ''}`}
					onClick={() => onClick(false)}
					aria-hidden="true"
				/>
			)}
			<nav className={sidebarClasses} aria-label="Main navigation" data-tour="main-nav">
				{/* Logo + collapse toggle */}
				<div className={styles.sidebarLogoRow}>
					<Link to={{ pathname: ROUTES.LANDING, search: searchParams.toString() }}>
						<Image
							src={Logo}
							alt="ReadyRx"
							loading="lazy"
							className={isBooleanTrue(collapsed) && isBooleanFalse(isMobile) ? styles.sidebarLogoHidden : styles.sidebarLogo}
						/>
						{isBooleanTrue(collapsed) && isBooleanFalse(isMobile) && (
							<span className={styles.sidebarLogoCollapsed}>
								R<sup>RX</sup>
							</span>
						)}
					</Link>
					{isBooleanFalse(isMobile) && (
						<button
							type="button"
							className={styles.sidebarToggleButton}
							onClick={onToggleCollapse}
							aria-label={isBooleanTrue(collapsed) ? 'Expand sidebar' : 'Collapse sidebar'}
						>
							<CollapseExpandIcon aria-hidden="true" />
						</button>
					)}
				</div>

				{/* Nav items */}
				<ul className={styles.sidebarNav}>
					{flatMenuItems.map((menu, index) => renderNavItem(menu, index))}
				</ul>

				</nav>
		</React.Fragment>
	);
};

export default NavbarVertical;
